import {
  useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef,
} from 'react';
import { fetchCaptcha } from '../../api/captchaApi';
import '../../assets/styles/CaptchaWidget.css';

export interface CaptchaWidgetRef {
  /** Load a CAPTCHA that was returned inside a failure response body. */
  loadCaptcha: (captchaId: string, imageBase64: string) => void;
  /** Clear the text input without re-fetching. */
  resetInput: () => void;
}

interface CaptchaWidgetProps {
  jwtToken:  string;
  onChange:  (captchaId: string, enteredCaptcha: string) => void;
  error?:    string | null;
  disabled?: boolean;
}

const CaptchaWidget = forwardRef<CaptchaWidgetRef, CaptchaWidgetProps>(
  ({ jwtToken, onChange, error, disabled }, ref) => {
    const [captchaId,   setCaptchaId]   = useState('');
    const [imageBase64, setImageBase64] = useState('');
    const [entered,     setEntered]     = useState('');
    const [loading,     setLoading]     = useState(false);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const load = useCallback(async () => {
      setLoading(true);
      setEntered('');
      try {
        const data = await fetchCaptcha(jwtToken);
        setCaptchaId(data.captchaId);
        setImageBase64(data.imageBase64);
        onChangeRef.current(data.captchaId, '');
      } catch (err) {
        /* console.error('Failed to load CAPTCHA:', err); */
        throw err;
      } finally {
        setLoading(false);
      }
    }, [jwtToken]);

    useEffect(() => { load(); }, [load]);

    useImperativeHandle(ref, () => ({
      loadCaptcha(id: string, img: string) {
        setCaptchaId(id);
        setImageBase64(img);
        setEntered('');
        onChangeRef.current(id, '');
      },
      resetInput() {
        setEntered('');
        onChangeRef.current(captchaId, '');
      },
    }));

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setEntered(val);
      onChangeRef.current(captchaId, val);
    };

    return (
      <div className="captcha-widget">
        <div className="captcha-widget__image-row">
          {loading ? (
            <div className="captcha-widget__image-placeholder">Loading…</div>
          ) : imageBase64 ? (
            <img
              src={imageBase64}
              alt="CAPTCHA challenge"
              className="captcha-widget__image"
              draggable={false}
            />
          ) : null}
          <button
            type="button"
            className="captcha-widget__refresh"
            onClick={load}
            disabled={disabled || loading}
            aria-label="Refresh CAPTCHA"
            title="Get a new CAPTCHA image"
          >
            ↻
          </button>
        </div>

        <input
          type="text"
          value={entered}
          onChange={handleInput}
          placeholder="Type the characters above"
          maxLength={5}
          disabled={disabled || loading}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="CAPTCHA answer"
          aria-invalid={!!error}
          className={`captcha-widget__input${error ? ' captcha-widget__input--error' : ''}`}
        />

        {error && (
          <span className="form-field__error" role="alert">{error}</span>
        )}
      </div>
    );
  }
);

CaptchaWidget.displayName = 'CaptchaWidget';
export default CaptchaWidget;
